import { takeEvery, select, put } from 'redux-saga/effects';
import { FormRenderProps } from 'react-final-form';
import { ActionType } from 'typesafe-actions';

import { postFeedback } from 'ApiPackage/FeedbackModule/services/feedback';
import { IResponseError } from 'ApiPackage/MainModule/interfaces/IResponseError';
import { fetchUploadFile } from 'ApiPackage/UploadModule/services/uploadApi';
import { captchaSetIsActive } from 'CorePackage/CaptchaModule/actions/captchaActions';
import { getCaptchaToken } from 'CorePackage/CaptchaModule/selectors/captchaSelectors';
import { coreShowNotice } from 'CorePackage/MainModule/actions/coreActions';
import { FEEDBACK_SEND_SUCCESSFULLY, INTERNAL_SERVER_ERROR } from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/MainModule/enums/NoticeTypesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IUploadFile } from 'CorePackage/MainModule/interfaces/IUploadFile';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import { FeedbackActionsEnum } from '../enums/FeedbackActionsEnum';
import { IFeedbackForm } from '../interfaces/IFeedbackForm';
import { getFeedbackFiles } from '../reducers/feedbackReducer';
import { feedbackSetFiles, feedbackUploadFile } from '../actions/feedbackActions';

const getUploadedFileIds = (uploadedFiles: IUploadFile[]) =>
  uploadedFiles.reduce((ids, { fileId }) => {
    if (fileId) {
      const currentArr = ids;
      return [fileId, ...currentArr];
    }
    return ids;
  }, []);

function* feedbackPostSaga(
  action: ActionType<{
    payload: { values: IFeedbackForm; textDescription: string; form?: FormRenderProps<IFeedbackForm>['form'] };
  }>,
) {
  function* funcTry() {
    const { values, textDescription, form } = action.payload;
    const state: IRootState = yield select();
    const captchaToken = getCaptchaToken(state);
    const files = getFeedbackFiles(state);
    yield postFeedback({
      declarantType: values.applicantType ? values.applicantType.id : null,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      organizationName: values.organizationName,
      position: values.position,
      patronymic: values.patronymic,
      phone: values.phone ? `+7${values.phone}` : '',
      text: textDescription,
      topic: values.topic,
      attachmentIds: getUploadedFileIds(files),
      knmId: values.knmId || null,
      captchaToken,
    });
    yield put(coreShowNotice(FEEDBACK_SEND_SUCCESSFULLY));
    yield put(feedbackSetFiles([]));
    if (values.knmId) {
      form.change('declarantType');
      form.change('applicantType');
      form.change('email');
      form.change('firstName');
      form.change('lastName');
      form.change('organizationName');
      form.change('patronymic');
      form.change('phone');
      form.change('text');
      form.change('captchaToken');
    } else {
      form?.reset();
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      const { status } = error;
      if (status === 403) {
        yield put(captchaSetIsActive(true));
      } else {
        yield put(coreShowNotice(INTERNAL_SERVER_ERROR, NoticeTypesEnum.ERROR));
      }
    },
  });
}

function* feedbackUploadFileSaga(action: ActionType<typeof feedbackUploadFile>) {
  function* funcTry() {
    const files: IUploadFile[] = yield select(getFeedbackFiles);
    const { e, nameIfExists } = action.payload;
    const fileList = e.target.files || [];
    const filesArray = Array.from(fileList);
    const file = filesArray[0];
    const fileName = nameIfExists || file.name;
    const fileType = file.type;
    const fileSize = file.size;
    const data = new FormData();
    data.append('file', file);
    const uploadingFiles = [
      ...files,
      {
        fileId: null,
        fileName,
        fileSize,
        fileType,
        progress: 0,
      },
    ];
    yield put(feedbackSetFiles(uploadingFiles));
    const fileId: number = yield fetchUploadFile(data);
    const fileIndex = uploadingFiles.findIndex(f => f.fileName === fileName);
    const uploadedFiles = [
      ...uploadingFiles.slice(0, fileIndex),
      {
        ...uploadingFiles[fileIndex],
        fileName,
        fileId,
        progress: 100,
        success: true,
      },
      ...uploadingFiles.slice(fileIndex + 1),
    ];
    yield put(feedbackSetFiles(uploadedFiles));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(coreShowNotice(INTERNAL_SERVER_ERROR, NoticeTypesEnum.ERROR));
    },
  });
}

export function* watchFeedbackSaga() {
  yield takeEvery(FeedbackActionsEnum.FEEDBACK_POST, feedbackPostSaga);
  yield takeEvery(FeedbackActionsEnum.FEEDBACK_UPLOAD_FILE, feedbackUploadFileSaga);
}
